import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import { RootState } from 'app/store'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import { listKaapiMachineDeployments } from 'k8s/components/kaapi/machine-deployment/actions'
import ModalForm from 'pf9-ui-components/built/elements/modal/ModalForm'
import Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import { prop } from 'ramda'
import { useSelector } from 'react-redux'
import PhysicalNodesTable from '../cluster/deployment/form-components/PhysicalNodesTable'
import { kaapiMachineDeploymentsForClusterSelector } from '../kaapi/machine-deployment/selectors'
import {
  listByoHosts,
  addClusterLabelToByoHost,
  removeClusterLabelToByoHost,
} from '../nodes/byoh/actions'
import { updateNodePools } from '../kaapi/clusters/actions'
import { ProgressStatus, StepProgress } from 'core/components/progress/StepProgress'
import { ProgressStep, useClusterProgress } from '../access-control/hooks/useClusterProgress'
import DataKeys from 'k8s/DataKeys'
import { waitSeconds } from 'app/plugins/openstack/components/infrastructure/blueprint/helpers'
import ApiClient from 'api-client/ApiClient'

const { kubernetes } = ApiClient.getInstance()

const initialValues = {
  physicalNodes: [],
}

interface AddNodeGroupsModalProps {
  onClose: () => void
  clusterName: string
}

const AddNodeGroupsModal = ({ onClose, clusterName }: AddNodeGroupsModalProps) => {
  const classes = useStyles()
  const { params, getParamsUpdater } = useParams<Record<string, any>>(initialValues)
  const { activeKaapiTenant } = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const namespace = activeKaapiTenant

  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [showProgress, setShowProgress] = useState(false)
  const [isProgressComplete, setIsProgressComplete] = useState(false)
  const {
    progressDetails,
    updateProgress,
    resetProgress: resetClusterProgress,
  } = useClusterProgress({
    initLabel: 'Updating Cluster',
  })

  // Fetch existing Machine Deployments
  const { reload: reloadMachineDeployments } = useListAction(listKaapiMachineDeployments)
  const machineDeploymentsSelector = kaapiMachineDeploymentsForClusterSelector({
    clusterName,
  })
  const machineDeployments = useSelector(machineDeploymentsSelector) || []
  const { reload: reloadByoHosts } = useListAction(listByoHosts)

  // Update Actions
  const {
    update: updateNodePool,
    updating: updatingNodePool,
    error: updateNodePoolError,
  } = useUpdateAction(updateNodePools)
  const {
    update: addClusterLabelToByoHostFn,
    updating: addClusterLabelToByoHostUpdating,
    error: addClusterLabelToByoHostError,
  } = useUpdateAction(addClusterLabelToByoHost)
  const {
    update: removeClusterLabelToByoHostFn,
    updating: removeClusterLabelToByoHostUpdating,
    error: removeClusterLabelToByoHostError,
  } = useUpdateAction(removeClusterLabelToByoHost)

  const hasError =
    !!addClusterLabelToByoHostError ||
    !!removeClusterLabelToByoHostError ||
    !!updateNodePoolError ||
    !!error

  useEffect(() => {
    if (!hasError) return
    // Show first error
    const errorObj =
      addClusterLabelToByoHostError ||
      removeClusterLabelToByoHostError ||
      updateNodePoolError ||
      error

    setError({
      title: errorObj?.title || 'Error adding new nodes',
      message: errorObj?.message || 'An error occurred while adding new nodes. Please try again.',
    })
  }, [
    hasError,
    addClusterLabelToByoHostError,
    removeClusterLabelToByoHostError,
    updateNodePoolError,
    error,
  ])

  const handleError = useCallback(
    (error: any, message: string, step?: ProgressStep) => {
      setLoading(false)
      setError(error || { title: 'Error', message })
      if (step) {
        updateProgress(step, ProgressStatus.Failed)
      }
    },
    [updateProgress],
  )

  const updatedReplicas = useMemo(
    () => machineDeployments[0]?.spec?.replicas + params?.physicalNodes?.length,
    [machineDeployments, params?.physicalNodes],
  )

  const handleSubmit = async () => {
    setError(null)
    setLoading(true)
    setShowProgress(true)

    // Check if physical nodes are provided
    if (params.physicalNodes.length === 0) {
      handleError(null, 'Please select atleast one physical node', ProgressStep.Init)
      return
    }

    // Update the selected nodes
    const res = await Promise.all(
      params.physicalNodes.map((node) =>
        addClusterLabelToByoHostFn({ name: node?.name, clusterName }),
      ),
    )
    // If any of the promises failed, return
    if (res?.some((item) => !item?.success)) {
      handleError(null, 'Failed to update labels on physical nodes', ProgressStep.Init)
      return
    }

    updateProgress(ProgressStep.Init, ProgressStatus.Success)

    // Update the machine deployment
    const { success } = await updateNodePool({
      namespace,
      clusterName,
      body: [
        {
          op: 'replace',
          path: `/spec/topology/workers/machineDeployments/0/replicas`,
          value: updatedReplicas,
        },
      ],
    })

    if (success) {
      updateProgress(ProgressStep.ControlPlane, ProgressStatus.Success)
      let isNodeGroupUpdated = false
      const maxAttempts = 60 // 5 minutes timeout (60 * 5 seconds)
      let attempts = 0

      while (!isNodeGroupUpdated && attempts < maxAttempts) {
        attempts++
        const updatedMachineDeployments = await kubernetes.getAllCustomResources(
          DataKeys.KaapiMachineDeployments,
        )

        const nodeGroupUpdated = updatedMachineDeployments?.find(
          (md) => md?.metadata?.name === machineDeployments?.[0]?.metadata?.name,
        )

        if (updatedReplicas === nodeGroupUpdated?.spec?.replicas) {
          isNodeGroupUpdated = true
          updateProgress(ProgressStep.NodeGroups, ProgressStatus.Success)
          setIsProgressComplete(true)
          setLoading(false)
        } else {
          await waitSeconds(5)
        }
      }
      if (!isNodeGroupUpdated) {
        setLoading(false)
        throw new Error('Node groups are taking too long to get deployed')
      }
    } else {
      await Promise.all(
        params.physicalNodes.map((node) => removeClusterLabelToByoHostFn({ name: node?.name })),
      )
      handleError(null, 'Failed to update labels on physical nodes', ProgressStep.ControlPlane)
    }

    if (!hasError) {
      // Reload the machine deployments and machines
      reloadByoHosts(true, false)
      reloadMachineDeployments(true, false)
    }
  }

  const submitting =
    addClusterLabelToByoHostUpdating ||
    removeClusterLabelToByoHostUpdating ||
    updatingNodePool ||
    loading

  const handleClose = useCallback(() => {
    setIsProgressComplete(false)
    setShowProgress(false)
    onClose()
  }, [onClose])

  const resetProgress = useCallback(() => {
    setIsProgressComplete(false)
    setShowProgress(false)
    resetClusterProgress()
  }, [resetClusterProgress])

  return (
    <ModalForm
      open
      title="Add Physical Nodes"
      maxWidth={820}
      className={classes.modal}
      onSubmit={showProgress || isProgressComplete ? handleClose : handleSubmit}
      submitTitle={showProgress || isProgressComplete ? 'Finish' : 'Add Physical Nodes'}
      error={error}
      submitting={submitting}
      disableSubmit={params.physicalNodes.length === 0}
      onClose={onClose}
      onBackButtonClick={resetProgress}
      showBackButton={error && showProgress}
      data-testid="add-physical-nodes-modal"
    >
      {!showProgress && !isProgressComplete ? (
        <>
          <Text variant="body2">Select the physical nodes to add to the cluster</Text>
          <br />
          <PhysicalNodesTable
            selectedNodes={params.physicalNodes}
            getParamsUpdater={getParamsUpdater}
          />
        </>
      ) : (
        <StepProgress
          progressDetails={progressDetails}
          title={`Node group update is in progress. 
                    Please wait and do not close this dialog, or the update will be cancelled.`}
        />
      )}
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  modal: {
    '& .modal-body': {
      padding: '32px 24px 24px 32px',
    },
    '& .modal-body .progress-root .progressContent > form': {
      marginBottom: 0,
    },
  },
}))

export default AddNodeGroupsModal
